import { put, select, all } from 'redux-saga/effects';
import { states } from '@/constants/lifecycle';
import findResult, {
  successAndErroredResponses,
  successAndErrors,
  findWithReadStatus,
  findWithCode, removeEmpty,
} from '@/utils/resultsHelper';
import selectors from '@/selectors';
import memberEntities from './constants';
import MembershipService from './service';
import { filterUsersCreatedandUpdated, membersDiff } from '../helper';

const sortMembersByEmail = (a, b) => {
  if (a.entity < b.entity) return 1;
  if (a.entity > b.entity) return -1;
  if (a.email < b.email) return -1;
  if (a.email > b.email) return 1;
  return 0;
};

export const sortMembersByNameThenEmail = (a, b) => {
  if (a.entity < b.entity) return 1;
  if (a.entity > b.entity) return -1;
  if (a.name < b.name) return -1;
  if (a.name > b.name) return 1;
  if (a.email < b.email) return -1;
  if (a.email > b.email) return 1;
  return 0;
};

export const filterErroredMembers = (membersList, createdItems) => membersList.filter((u) => !createdItems.errors.some(
  (e) => (e.uuid && e.uuid === u.uuid) || (e.code && e.code === u.code),
));

export const getMemberId = (member) => (member.entity === memberEntities.USER ? member.uuid : member.code);

const createMember = (entity, code, member) => MembershipService.createEntityMember(entity, code, member);

const deleteMember = (entity, code, member) => MembershipService.deleteEntityMember(entity, code, member);

const updateMember = (entity, code, member) => MembershipService.updateEntityMember(entity, code, member);

export function* applySharingToModel(entity, code, members, permissions) {
  const { currentUser } = yield select(selectors.auth);
  const diff = membersDiff(members.results, members.original, currentUser.id, permissions);

  // Create New Members
  const newResults = yield all(diff.newMembers.map((member) => createMember(entity, code, member)));
  const createdItems = successAndErrors(newResults);

  // Filter out all new members which failed creation
  const owners = filterErroredMembers(diff.updatedMembers.owners, createdItems);
  const updatedMembers = filterErroredMembers(diff.updatedMembers.members, createdItems);

  // Update all members to new permissions state
  const ownerResults = yield all(owners.map((member) => updateMember(entity, code, member)));
  const updatedResults = yield all(updatedMembers.map((member) => updateMember(entity, code, member)));
  const updatedMeResults = yield all(diff.updatedMembers.me.map((member) => updateMember(entity, code, member)));

  // Delete any members which were removed
  const removedResults = yield all(diff.removedMembers.members.map((member) => deleteMember(entity, code, member)));
  const removedMeResults = yield all(diff.removedMembers.me.map((member) => deleteMember(entity, code, member)));

  // Get all errors from deleting or updating
  const displayEntity = entity.charAt(0).toUpperCase() + entity.slice(1);
  const { success, errors } = successAndErroredResponses(
    removedResults.concat(removedMeResults),
    newResults,
    ownerResults.concat(updatedResults).concat(updatedMeResults),
    `${displayEntity} or member not found`,
  );

  // Determine number of successes
  const successMinusDuplicates = filterUsersCreatedandUpdated(success);
  const hasSuccess = successMinusDuplicates.length > 0;

  // Determine if user removed themselves
  const hasUserPermissions = members.results.some((user) => user.uuid === currentUser.id);
  const currentUserUserGroups = yield select(selectors.usersGroups);
  const currentUserUserGroupCodes = currentUserUserGroups ? currentUserUserGroups.map((group) => group.code) : [];
  const userRemoved = hasSuccess && success.some((user) => user.action === 'destroy' && user.uuid === currentUser.id);
  const userGroupRemoved = hasSuccess && !hasUserPermissions && success.some(
    (group) => group.action === 'destroy' && currentUserUserGroupCodes.includes(group.code),
  );
  const currentUserRemoved = userRemoved || userGroupRemoved;

  // Determine if user updated themselves
  let currentUserUpdated = false;
  if (hasSuccess && !currentUserRemoved) {
    currentUserUpdated = success.find((user) => user.action === 'update' && user.uuid === currentUser.id);
    if (!hasUserPermissions && !currentUserUpdated) {
      currentUserUpdated = success.find((group) => group.action === 'update'
          && currentUserUserGroupCodes.includes(group.code));
    }
  }

  return {
    errors,
    currentUserRemoved,
    currentUserUpdated,
    hasSuccess,
    successMinusDuplicates,
  };
}

export function* shareAPISaga(
  entityTypeString,
  entityPermissions,
  entityLists,
  sharingSelector,
  modelSelector,
  errorActionGenerator,
  successActionGenerator,
  refreshEntity,
  destroyEntity,
  updateCurrentPermissions,
) {
  const item = yield select(sharingSelector);
  const items = yield select(modelSelector);
  const { members, name } = findResult(items, item.category, item.code);

  const {
    errors,
    currentUserRemoved,
    currentUserUpdated,
    hasSuccess,
    successMinusDuplicates,
  } = yield applySharingToModel(entityTypeString, item.code, members, entityPermissions.SECURITY);

  if (errors.length > 0) {
    yield put(errorActionGenerator(item.code, name, errors));
  }

  if (currentUserRemoved) {
    const categories = findWithCode(items, entityLists, item.code);
    const toRefresh = findWithReadStatus(items, categories, states.SUCCESS);
    yield all(toRefresh.map((category) => refreshEntity({ category })));
    yield put(destroyEntity([{ code: item.code }], true));
  } else if (currentUserUpdated) {
    yield put(updateCurrentPermissions(item.code, currentUserUpdated.permissions));
  }

  if (hasSuccess) {
    yield put(successActionGenerator(item.code, name, successMinusDuplicates, currentUserRemoved));
  }
}

export const membersToAddChange = (members, value) => (
  removeEmpty(value.map((v) => members.find(({ uuid, code }) => [uuid, code].includes(v))))
);

export default sortMembersByEmail;
